import React, {useMemo} from 'react';
import {v4 as uuid} from 'uuid';
import {Button} from '../../components/ui/button';
import {PlusIcon} from 'lucide-react';
import {ServiceConfigCard} from '../components/ServiceConfigCard';
import useServiceConfigsValidation from '../hooks/useServiceConfigsValidation';
import { SCHED_MODES } from '../EmployeeConfigPanel'; 
/**
 * Step-2 of the wizard – lets the user create/update scheduling configs
 *
 * PROPS
 * ──────────────────────────────────────────────────────────────────────────────
 * services : array   – all services fetched once up-front
 * configs  : array   – state slice held by the parent (schedule.serviceSchedules)
 * setConfigs         – parent’s state setter
 * onSave             – callback once validation passes
 */
export default function Step2Scheduling({
  services,
  configs,
  setConfigs = () => {},
  onSave = () => {},
  hasGlobalErrors = false,
}) {
  /* ───────── helpers ─────────────────────────────────────────────────────── */
  const safeServices = Array.isArray(services) ? services : [];
  const safeSetConfigs = updater =>
    setConfigs(prev => {
      const current = Array.isArray(prev) ? prev : [];
      return typeof updater === 'function' ? updater(current) : updater;
    });

  const upsert = cfg =>
    safeSetConfigs(prev => {
      const idx = prev.findIndex(x => x.id === cfg.id);
      return idx === -1
        ? [...prev, cfg]
        : [...prev.slice(0, idx), cfg, ...prev.slice(idx + 1)];
    });

  const del = id => safeSetConfigs(prev => prev.filter(c => c.id !== id));

  const add = () =>
    upsert({
      id: uuid(),
      expanded: true,
      applyToAll: false,
      mode: 'EMPLOYEE_MANAGED',
      employeeManagedConfig: {bookingWindowWeeks: 3, maxConcurrent: 1},
      businessManagedConfig: {uploadEveryNDays: 7},
      services: [],
    });

  /* ───────── validation & memoised helpers ──────────────────────────────── */
  const { errors, validateAll } = useServiceConfigsValidation(configs, services);

  const usedServiceIds = useMemo(() => {
    const map = {};
    configs.forEach(c => {
      if (c.applyToAll) return;
      c.services?.forEach(id => (map[id] = (map[id] || 0) + 1));
    });
    return map;
  }, [configs]);

  const hasApplyAll = configs.some(c => c.applyToAll);
  const hasMaxConfigs = configs?.length >= services?.length;
  const doSave = () => {
    if (validateAll()) onSave();
  };
  /* Is there at least one BUSINESS_MANAGED service-config? */
  const hasBusinessManagedConfig = configs?.some(
    (c) => c.mode === SCHED_MODES.BUSINESS_MANAGED
  );
  /* ───────── UI ──────────────────────────────────────────────────────────── */
  return (
    <div className="space-y-8">
      {/* header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Service Scheduling Configs</h2>
        <Button
          onClick={add}
          disabled={hasApplyAll || hasMaxConfigs}
          title={
            hasApplyAll
              ? "“Apply to all” already exists"
              : hasMaxConfigs
                ? `You already have ${services.length} configs (one per service)`
                : ""
          }
        >
          <PlusIcon size={16} className="mr-2" />
          Add Config
        </Button>
      </div>
      {/* 🔹 Show global coverage / duplicate / limit errors */}
      <div className="space-y-1">
        {errors
          .filter(e => e.id === '*')
          .map((e, i) => (
            <div key={i} className="text-red-600 font-medium">
              {e.msg}
            </div>
          ))}
      </div>
      {/* configs grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {configs.map(c => {
          const disabledIds = Object.keys(usedServiceIds).filter(
            id => !c.services.includes(id),
          );
          const err = errors.find(e => e.id === c.id)?.msg;
          return (
            <ServiceConfigCard
              key={c.id}
              config={c}
              services={services}
              onUpdate={upsert}
              onDelete={del}
              error={err}
              disabledServiceIds={disabledIds}
            />
          );
        })}
      </div>

     {/* footer */}
      {errors.length === 0 &&
        !hasBusinessManagedConfig &&
        !hasGlobalErrors && (   
          <div className="flex justify-end pt-6 border-t">
          <Button onClick={doSave}>Save Configuration</Button>
        </div>
      )}
    </div>
  );
}
